/**
 * RTWO Cryptography & Identity Engine — Public Key Validation Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { CryptoAlgorithm, EllipticCurve, PublicKeyHex } from '../domain-models';
import type { IPublicKeyValidationService, PublicKeyValidationResult } from '../service-interfaces';
import type { KeyProviderRegistry } from '../registry/provider-registries';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

const HEX_PATTERN = /^[0-9a-fA-F]+$/;

export class PublicKeyValidationService
  extends BaseCryptoService
  implements IPublicKeyValidationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.PUBLIC_KEY_VALIDATION;
  readonly serviceName = 'Public Key Validation Service';
  readonly description = 'Validates public key format via standard cryptographic abstractions.';
  readonly layer = 'R6' as const;
  readonly integrations = ['R6 Security & Automation'];
  readonly requiresKeyProvider = true;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly keyProviderRegistry: KeyProviderRegistry
  ) {
    super(context);
  }

  async validatePublicKey(
    publicKey: PublicKeyHex,
    algorithm: CryptoAlgorithm
  ): Promise<PublicKeyValidationResult> {
    if (!publicKey || !HEX_PATTERN.test(publicKey)) {
      return {
        valid: false,
        algorithm: null,
        curve: null,
        error: 'Public key must be a non-empty hexadecimal string.',
        isPlaceholder: true,
        source: 'Key Provider — Invalid Input',
      };
    }

    const provider = this.keyProviderRegistry.getActiveProvider();
    if (!this.keyProviderRegistry.isConfigured() || !provider.isReady()) {
      return {
        valid: false,
        algorithm: null,
        curve: null,
        error: 'Public key validation unavailable. Key Provider not configured or not connected.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    const curveMap: Record<CryptoAlgorithm, EllipticCurve> = {
      Ed25519: 'Ed25519',
      secp256k1: 'secp256k1',
      secp256r1: 'secp256r1',
      'BLS12-381': 'BLS12-381',
      sr25519: 'Ristretto255',
    };
    const curve = curveMap[algorithm];

    const valid = await provider.validatePublicKey(publicKey, algorithm, curve);

    return {
      valid,
      algorithm,
      curve,
      error: valid ? null : 'Public key failed cryptographic validation.',
      source: valid ? 'Key Provider — Public Key Valid' : 'Key Provider — Public Key Invalid',
    };
  }
}
